import { useNuxtApp } from "#app";

/* ====================================================================
 *  useTracking — єдина точка виходу аналітики: GA4 + Meta Pixel.
 *
 *  Обидва канали безпечні до відсутності провайдера:
 *  - $ga лениво ініціалізує Firebase Analytics (plugins/firebase.client.ts);
 *  - $metaPixel provide-иться ЛИШЕ на прод-домені з увімкненою рекламою
 *    (plugins/meta-pixel.client.ts), в усіх інших середовищах pixel() — no-op.
 *  Повернути рекламу = NUXT_PUBLIC_ENABLE_ADS=true, без змін у коді.
 *
 *  Дедуплікація подій (напр. Purchase "рівно один раз на замовлення")
 *  залишається на боці викликача — модуль лише транспорт.
 * ==================================================================== */

type EventParams = Record<string, unknown>;

interface GaItemInput {
  /** product-подібний об'єкт: id/title/categorySlug */
  product: unknown;
  /** variant-подібний об'єкт: sku/id; null — без варіанта */
  variant?: unknown;
  /**
   * Частини item_variant, коли у варіанта немає sku/id (колір, розмір).
   * Викликач сам вирішує джерело (variant-поля чи вибір користувача) —
   * білдер не вгадує, щоб payload лишався байт-у-байт як раніше.
   */
  variantFallback?: Array<string | null | undefined>;
  price: number;
  quantity?: number;
  /** item_list_name / item_list_id — тільки для list-контекстів (картки) */
  list?: { name: string; id: string };
}

/**
 * Єдиний конструктор GA4 e-commerce item.
 * Використовується ProductCard, useProductCart, useProductTracking,
 * каталогом (view_item_list). Pixel-payload'и НЕ уніфікуються — вони
 * навмисно живуть у викликачів (різні джерела currency/value).
 */
export function buildGaItem({
  product,
  variant,
  variantFallback = [],
  price,
  quantity = 1,
  list,
}: GaItemInput): EventParams {
  const p = (product || {}) as Record<string, unknown>;
  const v = (variant || null) as Record<string, unknown> | null;

  const item: EventParams = {
    item_id: String(p.id || ""),
    item_name: String(p.title || ""),
    item_brand: "Mulberry'SS",
    item_category: String(p.categorySlug || ""),
    item_variant:
      v?.sku ||
      v?.id ||
      variantFallback.filter(Boolean).join(" / ") ||
      undefined,
    price: Number(price || 0),
    quantity: Number(quantity || 1),
  };

  if (list) {
    item.item_list_name = list.name;
    item.item_list_id = list.id;
  }

  return item;
}

/**
 * Позиції для `view_item_list` із карток гріда каталогу. Формує рівно
 * той payload, який каталог слав і раніше: перші 24 позиції, ціна
 * варіанта з фолбеком на товар, `index` з одиниці.
 */
export function buildGaListItems(
  items: Array<{ product?: unknown; variant?: unknown }>,
  list: { name: string; id: string },
): EventParams[] {
  return (items || []).slice(0, 24).map((x, idx) => {
    const p = (x.product || {}) as Record<string, unknown>;
    const v = (x.variant || null) as Record<string, unknown> | null;
    const color = v?.color as Record<string, unknown> | string | undefined;
    return {
      ...buildGaItem({
        product: p,
        variant: v,
        variantFallback: [
          (color as Record<string, unknown>)?.value ?? color,
          v?.size,
        ] as Array<string | null | undefined>,
        price: Number(v?.price ?? p.price ?? 0),
        quantity: 1,
        list,
      }),
      index: idx + 1,
    };
  });
}

export interface PixelContentInput {
  productId?: unknown;
  key?: unknown;
  qty?: unknown;
  price?: unknown;
}

export interface PixelContents {
  content_ids: string[];
  contents: { id: string; quantity: number; item_price: number }[];
  num_items: number;
}

/**
 * Спільна частина pixel-payload'ів: перелік позицій. Envelope
 * (currency/value/event) НАВМИСНО лишається у викликачів — джерела суми
 * різні (кошик рахує сам, замовлення бере order.total).
 * Споживачі: InitiateCheckout (кошик), Purchase (сторінка замовлення).
 */
export function buildPixelContents(
  items: PixelContentInput[] | null | undefined,
): PixelContents {
  const contents = (Array.isArray(items) ? items : [])
    .map((i) => ({
      id: String(i?.productId || i?.key || ""),
      quantity: Number(i?.qty || 1),
      item_price: Number(i?.price || 0),
    }))
    .filter((x) => x.id);

  return {
    content_ids: contents.map((c) => c.id),
    contents,
    num_items: contents.reduce((s, c) => s + Number(c.quantity || 0), 0),
  };
}

interface MetaPixelApi {
  track?: (name: string, params?: EventParams, options?: EventParams) => void;
}

export function useTracking() {
  const nuxtApp = useNuxtApp();
  // $metaPixel provide-иться імперативно, тому його немає в згенерованих типах
  const $metaPixel = (nuxtApp as unknown as { $metaPixel?: MetaPixelApi })
    .$metaPixel;
  const { $ga } = nuxtApp;

  function ga(name: string, params?: EventParams) {
    try {
      $ga?.log?.(name, params);
    } catch {
      /* never break UX because of analytics */
    }
  }

  function pixel(name: string, params?: EventParams, options?: EventParams) {
    try {
      $metaPixel?.track?.(name, params, options);
    } catch {
      /* never break UX because of analytics */
    }
  }

  return { ga, pixel };
}
